import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { getTestTypeValueFromUrl, getTestName } from "../../utils";
import { phlebotomyAssessmentStatusUrl } from "../../utils/constants";
import useApis from "../../services/useAPI";
import { Button } from "../../stories/Buttons/Button";
import { Alert, Modal } from "react-bootstrap";
import { useAuth } from "../../components/AuthContext/AuthContext";
import checkIcon from "../../assets/icons/check-icon.svg";
import warning from "../../assets/icons/phlebotomy-warning.svg";
import CancelCurrentAppointment from "./CancelCurrentAppointment";
import { XSquare } from "react-bootstrap-icons";
/**
 * SuccessPage component
 * Displays the success message after completing a registration for the phlebotomy test.
 * It includes options to view or edit registration details, and an option to cancel the registration.
 *
 * @returns {JSX.Element} The rendered page displaying confirmation, booking details, and cancellation options.
 */

const SuccessPage = () => {
  const [details, setDetails] = useState(null);
  const { test_type, bid } = useParams();
  const [modalOpen, setModalOpen] = useState(false);
  const { callApi, resultGet, resultPost, serverError } = useApis();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const [showCancelComponent, setShowCancelComponent] = useState(false);

  // Toggles modal visibility
  const toggleModal = () => setModalOpen(!modalOpen);

  // Handles the API call to reject a booking
  const rejectBooking = async () => {
    try {
      await callApi({}, `/api/dashboard/reject-booking/${bid}/`, "POST");
    } catch (error) {
      auth?.showToast(
        "Reject Booking",
        "An error occurred while rejecting the booking.",
        "../assets/mhc.png",
      );
    }
  };

  useEffect(() => {
    if (serverError) {
      auth?.showToast("Error", serverError, "../assets/mhc.png", <XSquare />);
    }
  }, [resultGet, serverError]);


  // Handles post-rejection behavior
  useEffect(() => {
    if (resultPost) {
      const message =
        resultPost?.status === "rejected"
          ? "Your appointment has been cancelled successfully. You can proceed to book a new one now."
          : "Reject failed";
      auth?.showToast("Booking Cancelled", message, "../assets/mhc.png");
      if (resultPost?.status === "rejected") {
        navigate(
          `/bloods/boots/${test_type ||
            localStorage.getItem("test_type")}?rejected=true`,
        );
      }
    }
  }, [resultPost, auth, navigate, test_type]);

  // Opens the modal for confirmation
  const handleRejectBooking = () => toggleModal();

  //Handles reject confirmation
  const handleConfirmReject = () => {
    rejectBooking();
    toggleModal();
  };

  // Fetches booking details when the component loads
  useEffect(() => {
    const fetchDetails = async () => {
      await callApi(null, phlebotomyAssessmentStatusUrl(bid), "GET");
    };
    fetchDetails();
  }, [bid]);

  // Updates the state with fetched booking details
  useEffect(() => {
    if (resultGet) {
      setDetails(resultGet?.phlebotomist_booking_info);
    }
  }, [resultGet]);

  // Navigates to the details page based on the booking status
  const viewEditDetails = () => {
    navigate(
      `/bloods/boots/assessment-details/${getTestTypeValueFromUrl(
        details?.test_type,
      )}/${bid}`,
    );
  };

  useEffect(() => {
    const resumeTestType = resultGet?.phlebotomist_booking_info?.test_type;
    const urlTestType = getTestTypeValueFromUrl(location.pathname.split("/success-page/")[1]?.split("/")[0]);

    if (!resumeTestType || !urlTestType) return;

    if (urlTestType !== resumeTestType) {
      setShowCancelComponent(true);
      return;
    }
    setShowCancelComponent(false);
  }, [location.pathname, resultGet?.phlebotomist_booking_info?.test_type, navigate, details]);

  if (showCancelComponent) {
    return (
      <CancelCurrentAppointment
        handleRejectBooking={handleRejectBooking}
        handleConfirmReject={handleConfirmReject}
        toggleModal={toggleModal}
        modalOpen={modalOpen}
        test_type={location.pathname.split("/boots/")[1]?.split("/")[0]}
        resumeTestType = {resultGet?.phlebotomist_booking_info?.test_type}
      />
    );
  }

  return (
    <div className="row justify-content-center appointment-confirmation-modal dashboard-page">
      <div className="col-md-12">
        {details ? (
          <>
            <div className="d-flex align-items-center success-heading">
              <img src={checkIcon} alt="success icon" />
              <h1 className="mb-0">Registration Complete!</h1>
            </div>
            <div className="row justify-content-center pb-3">
              <div className="col-md-12 ageo-normal-font">
                <p style={{ color: "#9A9797" }} className="ageo-medium detail-subtitle">Registration for your{" "}
                <b style={{ color: "#003032" }}>Boots In-store Wellness Testing Service</b>{" "}
                is now confirmed</p>

                <div className="booking-details mt-4">
                  <h4 className="booking-info-heading">Test Information</h4>

                  <div className="row g-0 booking-info">
                    <div className="row detail">
                      <div className="col-md-6 p-0 service-details">
                        <h5>Service</h5>
                        <h3 className="m-0">
                          {getTestName(
                            getTestTypeValueFromUrl(details.test_type),
                          )}
                        </h3>
                        <h4>In-Store Service</h4>
                      </div>

                      <div className="col-md-6 p-0">
                        <h5>Booking ID</h5>
                        <h3>
                          {" "}
                          <strong className="booking-id">
                            {details?.booking_status === "archived"
                              ? details?.booking_id + "-2"
                              : details?.booking_id}
                          </strong>
                        </h3>
                      </div>
                    </div>

                    <div className="row m-0 warning-details">
                      <div className="col-md-12 d-flex p-0">
                        <img src={warning} alt="Warning Icon" className="warning-icon" />
                        <h3 className="mb-0">A confirmation email has been sent to you</h3>
                      </div>
                      <div className="col-md-12 confirm-email-desc p-0">
                      <p className="mb-0">Please refer to it for the details you’ll need at your appointment.</p>
                      </div>
                    </div>
                    <div className="p-0 details-buttons">
                      <Button
                        className="btn-start-assessment me-4"
                        onClick={viewEditDetails}
                        label={
                          resultGet?.can_be_edited ? "Edit Details" : "View Details"
                        }
                      />
                      {details?.booking_status !== "rejected" &&
                        details?.booking_status !== "completed" && (
                              <Button
                                className="btn-white cancel-registration-btn"
                                label="Cancel registration"
                                onClick={handleRejectBooking}
                                variant="outline"
                              />
                        )}
                    </div>
                  </div>
                  
                </div>
              </div>
            </div>
          </>
        ) : null}
        <Modal
          className="confirm-rejection-modal"
          show={modalOpen}
          onHide={toggleModal}
        >
          <Modal.Header closeButton>
            <Modal.Title>Confirm Cancellation</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to cancel this appointment? This action cannot
            be undone.
          </Modal.Body>
          <Modal.Footer>
            <Button
              className="btn-gray"
              variant={"secondary"}
              onClick={toggleModal}
              label="Cancel"
            />
            <Button
              className="btn-primary"
              onClick={handleConfirmReject}
              label="Confirm"
            />
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default SuccessPage;
